import { isEqual } from "lodash";
import * as React from "react";
import { findDOMNode } from "react-dom";
import styled from "styled-components";

import { colors, viewports } from "../styles/variables";

import { rAFAnimate } from "../utils/rAFAnimate";

export const MENU_HEIGHT = 54;

/** in seconds */
const ANIMATION_DURATION = 0.3;

/** in pixels */
const INITIAL_SCROLL_LEFT = 20;

/**
 * If a selection is user-initiated (tap/click), we bounce every non-user-initiated selections for a short delay.
 * The user just clicked an item, it should stay selected for a while so the user can acknowledge the result of its action.
 */
const USER_INITIATED_HIGHLIGHT_DEBOUNCE = 1000;

const StyledMenu = styled.div`
  position: relative;
  top: 0;
  height: 54px;
  z-index: 200;
  overflow-x: scroll;
  overflow-y: hidden;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  user-select: none;
  -ms-overflow-style: -ms-autohiding-scrollbar;
  background: white;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  transform: translateZ(0);
  will-change: transform;

  ${viewports.desktop`
    max-width: 800px;
    margin: 0 auto;
    margin-top: -2px;
    border-radius: 0 0 5px 5px;
  `};
`;

const StyledMenuItemsContainer = styled.div`
  position: relative;
  margin-left: ${INITIAL_SCROLL_LEFT}px;
  white-space: nowrap;
  z-index: 205;
`;

type StyledMenuItemsContainer = typeof StyledMenuItemsContainer;

interface StyledMenuActiveIndicatorProps {
  indicatorWidth: number;
  indicatorTranslate: number;
}

const StyledMenuActiveIndicator = styled.div<StyledMenuActiveIndicatorProps>`
  position: absolute;
  z-index: 202;
  background-color: ${colors.brand};
  height: calc(100% - 26px);
  margin-top: 12px;
  width: ${props => props.indicatorWidth}px;
  top: 0;
  left: 0;
  transform-origin: top left;
  transition: width ${ANIMATION_DURATION}s linear, transform ${ANIMATION_DURATION}s linear;
  transform-style: flat;
  transform: translateX(${props => props.indicatorTranslate}px);
  border-radius: 30px;
  pointer-events: none;
  will-change: transform;
`;
type StyledMenuActiveIndicator = typeof StyledMenuActiveIndicator;

interface Props {
  items: string[];
  onMenuClick?: (index: number) => void;
}

interface State {
  activeIndex: number;
  indicatorWidth: number;
  indicatorTranslate: number;
}

/**
 * An interactive menu inspired by the one in the Uber Eats app
 */
export default class Menu extends React.Component<Props, State> {
  state = {
    activeIndex: 0,
    indicatorWidth: 1,
    indicatorTranslate: 0
  };

  private itemsRefs: MenuItem[] = [];
  private itemsContainerRef: any;
  private indicatorRef: any;

  private pending = false;
  private lastSelection = new Date();

  /** method to be called by other components */
  selectItem = (index: number) => {
    this.selectItemImpl(index, false);
  };

  selectItemImpl = (index: number, userSelection = false) => {
    if (index < 0 || index > this.props.items.length - 1) {
      console.warn("Index out of bound, no-op");
      return;
    }
    if (!userSelection && (this.pending || +this.lastSelection + USER_INITIATED_HIGHLIGHT_DEBOUNCE > +new Date())) {
      return;
    }

    if (userSelection) {
      this.lastSelection = new Date();
    }
    this.pending = true;

    const newItemNode: any = findDOMNode(this.itemsRefs[index]);
    const newItemRect = newItemNode.getBoundingClientRect();
    const itemsContainerNode: any = this.itemsContainerRef;
    const itemsContainerRect = itemsContainerNode.getBoundingClientRect();

    const scrollableNode: any = findDOMNode(this);

    const newItemPosition = Math.round(newItemRect.left - itemsContainerRect.left);
    const initialScrollLeft = scrollableNode.scrollLeft;

    // Only scrollLeft is animated in JS, other properties (width and translateX) are animated using CSS transitions
    // Animating both scrollLeft and translateX would allow us to better sync their position and give a more credible illusion (that the indicator position is fixed on the left) but yields a lower performance
    const scrollLeftAnimation = (percentage: number) => {
      const newScrollLeft = Math.round(initialScrollLeft + (newItemPosition - initialScrollLeft) * percentage);
      scrollableNode.scrollLeft = newScrollLeft;
    };

    // Manual scrolling must be deactivated during the scrolling to avoid glitches
    const beforeAnimation = () => {
      scrollableNode.style.overflowX = "hidden";
    };
    const afterAnimation = () => {
      scrollableNode.style.overflowX = "scroll";
      this.pending = false;
    };
    rAFAnimate(scrollLeftAnimation, beforeAnimation, afterAnimation, ANIMATION_DURATION, false);
    this.setState({
      activeIndex: index,
      indicatorTranslate: newItemPosition + INITIAL_SCROLL_LEFT,
      indicatorWidth: Math.floor(newItemRect.width)
    });
  };

  updateSelectItem = () => {
    this.selectItemImpl(this.state.activeIndex, true);
  };

  handleClick = (index: number) => {
    return () => {
      if (this.props.onMenuClick) {
        this.props.onMenuClick(index);
      }
      this.selectItemImpl(index, true);
    };
  };

  setItemRef = (index: number) => {
    return (ref: null | MenuItem) => {
      if (ref) {
        this.itemsRefs[index] = ref;
      }
    };
  };

  componentDidMount() {
    window.addEventListener("resize", this.updateSelectItem);
    this.selectItemImpl(this.state.activeIndex, true);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateSelectItem);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    if (
      nextState.indicatorWidth !== this.state.indicatorWidth ||
      nextState.activeIndex !== this.state.activeIndex ||
      nextState.indicatorTranslate !== nextState.indicatorTranslate
    ) {
      return true;
    }
    if (!isEqual(nextProps, this.props)) {
      return true;
    }
    return false;
  }

  render() {
    return (
      <StyledMenu>
        <StyledMenuItemsContainer ref={(ref: any) => (this.itemsContainerRef = ref)}>
          {this.props.items.map((text, index) => (
            <MenuItem
              key={index}
              text={text}
              onClick={this.handleClick(index)}
              active={index === this.state.activeIndex}
              ref={(ref: any) => (this.itemsRefs[index] = ref)}
            />
          ))}
          <SpacerItem />
        </StyledMenuItemsContainer>
        <StyledMenuActiveIndicator
          indicatorTranslate={this.state.indicatorTranslate}
          indicatorWidth={this.state.indicatorWidth}
          ref={(ref: any) => (this.indicatorRef = ref)}
        />
      </StyledMenu>
    );
  }
}

interface StyledMenuItemProps {
  active: boolean;
}

const StyledMenuItem = styled.div<StyledMenuItemProps>`
  display: inline-block;
  padding: 18px 10px;
  color: ${props => (props.active ? "white" : "black")}
  transition: color ${ANIMATION_DURATION}s ease-in-out;
  font-size: .8rem;
  cursor: pointer;
`;

interface MenuItemsProps {
  text: string;
  onClick: React.ReactEventHandler;
  active: boolean;
}

class MenuItem extends React.Component<MenuItemsProps, {}> {
  shouldComponentUpdate(nextProps: MenuItemsProps, _: {}) {
    return this.props.active !== nextProps.active;
  }

  render() {
    return (
      <StyledMenuItem onClick={this.props.onClick} active={this.props.active}>
        {this.props.text}
      </StyledMenuItem>
    );
  }
}

// Since margin-right cannot be used on the items container, we insert this fake item at the end
const SpacerItem = styled.div`
  display: inline-block;
  width: ${INITIAL_SCROLL_LEFT}px;
  font-size: 0.8rem;
`;



// WEBPACK FOOTER //
// ./src/components/Menu.tsx